import React, {
  useMemo,
  useRef,
  useCallback,
  useImperativeHandle,
  forwardRef,
  useEffect,
} from 'react';
import { formatAllDate, formatTime } from '@utils/parse-date';
import './SharePoster.scss';
import DefaultCover from '@assets/imgs/default_cover.png';
import html2canvas from 'html2canvas';
import { PosterItem } from './poster/PosterList';
import PosterTitle from './poster/PosterTitle';
import PosterHost from './poster/PosterHost';
import PosterCover from './poster/PosterCover';
import PosterTime from './poster/PosterTime';
import PosterLocation from './poster/PosterLocation';
import PosterQrcode from './poster/PosterQrcode';

interface SharePosterProps {
  activityInfo: any;
  className?: string;
  posterConfig: PosterItem;
  onCoverLoad?: () => void;
  formPage?: string;
}

export interface PosterRef {
  downloadPoster: (filename?: string) => void;
}

export const SavePosterTip: React.FC = () => (
  <div className="share-poster-footer">
    <div className="share-poster-footer-contain">
      <span>——</span>
      <span>长按海报保存到相册</span>
      <span>——</span>
    </div>
  </div>
);

export const loadPosterImg = (domRef: any) => {
  const canvas = document.createElement('canvas');
  if (domRef.current) {
    const { offsetHeight, offsetWidth } = domRef.current;
    const [cw, ch] = [
      window.devicePixelRatio * offsetWidth,
      window.devicePixelRatio * offsetHeight,
    ];
    canvas.style.width = `${offsetWidth}px`;
    canvas.style.height = `${offsetHeight}px`;
    canvas.width = cw;
    canvas.height = ch;

    return html2canvas(domRef.current, {
      backgroundColor: null,
      useCORS: true,
      canvas,
      imageTimeout: 0,
      timeout: 0,
      allowTaint: true,
      scrollY: true,
    })
      .then((canvas) => canvas.toDataURL('image/png'))
      .catch((e) => Promise.reject(e));
  }
  return Promise.reject('err');
};

export const getBetweenAllDate = (start: number, end: number) => {
  if (!start || !end || start < 0 || end < 0) {
    return '';
  }
  const startDate = formatAllDate(start);
  const endDate = formatAllDate(end);
  const startTime = formatTime(start);
  const endTime = formatTime(end);
  if (startDate === endDate)
    return (
      <span>
        {startDate}{' '}
        <span className="same-day-time">
          {startTime}-{endTime}
        </span>
      </span>
    );
  return (
    <span>
      {startDate} {startTime} <span className="separtor">-</span> {endDate} {endTime}
    </span>
  );
};
const SharePoster = forwardRef<PosterRef, SharePosterProps>((props, ref) => {
  const { activityInfo, className, posterConfig, onCoverLoad, formPage } = props;
  const { logo, title, host, cover, time, location, qrcode, posterId } = posterConfig;

  const domRef = useRef<HTMLDivElement>(null);

  const date = useMemo(
    () => getBetweenAllDate(activityInfo?.start_time, activityInfo?.end_time),
    [activityInfo],
  );

  const activityLink = useMemo(() => {
    let qrCodeUrl = '';
    if (typeof window !== 'undefined') {
      qrCodeUrl =
        activityInfo?.event_type === 0 && !activityInfo?.webinar_fields?.short_link
          ? `${window.location.origin}/events/host/event/${activityInfo?.event_id}/registration`
          : activityInfo?.webinar_fields?.short_link;
      if (formPage === 'lobby') {
        qrCodeUrl = `${window.location.origin}/events/lobby/${activityInfo?.event_id}`;
      }
    }
    return qrCodeUrl;
  }, [activityInfo]);

  const getPoster: () => Promise<Blob | null> = useCallback(
    () =>
      new Promise((resolve) => {
        if (domRef.current) {
          html2canvas(domRef.current, {
            x: 0,
            y: 0,
            scale: 3,
            useCORS: true,
            backgroundColor: 'transparent',
          }).then((canvas) => {
            if (canvas) {
              canvas.toBlob(
                (b) => {
                  resolve(b);
                },
                'image/png',
                1,
              );
            }
          });
        }
      }),
    [domRef],
  );

  const downloadPoster = async (filename = '海报') => {
    const blob = await getPoster();
    if (blob) {
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.style.display = 'none';
      link.href = url;
      link.download = `${filename}.png`;
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
    }
  };

  useImperativeHandle(
    ref,
    () => ({
      downloadPoster,
    }),
    [domRef],
  );
  useEffect(() => {
    if (!cover && onCoverLoad) {
      onCoverLoad();
    }
  }, [cover, onCoverLoad]);
  const decorationUint = (
    <span>
      <span>{new Date().getFullYear()}</span>
      <span>ACTIVITY</span>
    </span>
  );

  const decoration = new Array(10).fill(decorationUint);
  return (
    <div className={`share-poster ${className} poster-${posterId}`} ref={domRef}>
      <div className="share-poster-bg">
        {logo.show && (
          <div className="share-poster-logo">
            <img
              crossOrigin="anonymous"
              className="share-poster-logo-img"
              src={logo.src}
              draggable={false}
            />
          </div>
        )}
        <div className="share-poster-bg-decoration">{decoration}</div>
        <div className="share-poster-bg-shape"></div>
        {
          <PosterTitle
            title={activityInfo?.title}
            className="share-poster-title"
            line={title.line}
            posterId={posterId}
          />
        }
        {host.show && (
          <PosterHost
            host={activityInfo?.host_name}
            className="share-poster-host"
            extra={host.extra}
            line={host.line}
            posterId={posterId}
          />
        )}
        {cover && (
          <PosterCover
            src={activityInfo?.cover_url || DefaultCover}
            className={!activityInfo?.address ? 'no-location-cover' : ''}
            onCoverLoad={onCoverLoad}
          />
        )}
        <div
          className={`share-poster-detail-list ${!activityInfo?.address ? 'location-empty' : ''}`}
        >
          {time.show && <PosterTime time={date} className="share-poster-time" extra={time.extra} />}
          {location.show && activityInfo?.address && (
            <PosterLocation
              location={activityInfo?.address}
              className="share-poster-location"
              extra={location.extra}
              line={location.line}
              posterId={posterId}
            />
          )}
        </div>
        {<PosterQrcode type={qrcode.type} activityLink={activityLink} logo={qrcode.logo} />}
      </div>
    </div>
  );
});

SharePoster.displayName = 'SharePoster';

export default SharePoster;
